import { useLang } from "@/context/LanguageContext";
import { T } from "@/components/T";
import { Link } from "react-router-dom";
import { resolveNav } from "@/lib/nav";
import { useSettings } from "@/context/SettingsContext";
import { MandalaMotif } from "@/components/MotifBackground";
import { ArrowUpRight } from "lucide-react";

function DirectoryGroup({ group, motif }) {
  const { pick } = useLang();
  return (
    <section className="relative border-t border-line px-5 py-16 md:px-8 md:py-20">
      {motif && <MandalaMotif className="pointer-events-none absolute right-0 top-8 h-56 w-56 text-[var(--gold)] opacity-[0.05]" />}
      <div className="relative z-[2] mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <p className="overline-label mb-3">{pick(group.label.deva, group.label.en)}</p>
          <h2 className="font-serif-latin text-3xl leading-tight text-ink md:text-4xl">
            <span className="font-deva mr-3 text-saffron">{group.label.deva}</span>{pick(group.blurb.deva, group.blurb.en)}
          </h2>
        </div>
        <div className="mt-10 grid gap-px overflow-hidden border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {group.items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`directory-link-${it.to.replace(/\//g, "")}`}
              className="group relative flex flex-col bg-surface p-6 transition-colors duration-300 hover:bg-surface-alt"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-baseline gap-3">
                  <span className="font-deva text-2xl text-saffron deva-lh">{it.deva}</span>
                  <span className="min-w-0 font-serif-latin text-xl text-ink">{pick(it.deva, it.en)}</span>
                </div>
                {it.soon && (
                  <span className="shrink-0 rounded-sm border border-gold/40 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] text-gold"><T>Coming Soon</T></span>
                )}
              </div>
              {it.sub && <p className="mt-2 text-sm text-ink-3">{pick(it.sub.deva, it.sub.en)}</p>}
              <ArrowUpRight className="mt-4 h-4 w-4 text-ink-3 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-saffron" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ExplorePage() {
  const { pick } = useLang();
  const { isVisible, nav_config } = useSettings();

  const groups = resolveNav(nav_config?.directory)
    .map((g) => ({ ...g, items: g.items.filter((l) => l.to === "/explore" || isVisible(l.to)) }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-16 -top-8 h-72 w-72 text-[var(--gold)] opacity-[0.07]" />
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <p className="overline-label mb-3">श्रद्धा · ज्ञान · परंपरा · सुलभता</p>
          <h1 className="font-serif-latin text-4xl text-ink md:text-6xl">
            <T>The</T> <span className="font-deva text-saffron">धर्मार्थ</span> <T>Directory</T>
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink-2 md:text-lg">
            {pick(
              "धर्मार्थ का संपूर्ण मानचित्र — सीखें, साधना करें, भारत की धरोहर का अन्वेषण करें, मार्गदर्शन पाएँ, समुदाय से जुड़ें एवं सेवाएँ प्राप्त करें।",
              "One elegant map of everything on Dharmartha — learn, practise, explore Bharat's heritage, seek guidance, join the community and request services.")}
          </p>
        </div>
      </section>

      {groups.map((g, i) => <DirectoryGroup key={g.key} group={g} motif={i % 2 === 0} />)}
    </>
  );
}
